import React from 'react'
import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import ThemeConfig from '../../../theme/ThemeConfig'
import Typography from '../../Typography/Typography'
import { Alert, FieldId } from '../types/Alert'

const PREFIX = 'UngroupedAlert'

const classes = {
  container: `${PREFIX}-container`,
  cell: `${PREFIX}-cell`,
  typographyColor: `${PREFIX}-typographyColor`,
  notClickable: `${PREFIX}-notClickable`,
}
const hoverColor = '#F7F7F7'

const Root = styled('tr')({
  [`&.${classes.container}`]: {
    borderBottom: '1px solid',
    borderBottomColor: ThemeConfig.palette.secondary.main,
    outline: 'none',
    '&:hover': {
      backgroundColor: hoverColor,
      cursor: 'pointer',
    },
    '&$notClickable': {
      cursor: 'auto',
    },
  },
  [`& .${classes.cell}`]: {
    padding: '8px',
    maxWidth: '334px',
    overflowWrap: 'break-word',
  },
  [`& .${classes.typographyColor}`]: {
    color: ThemeConfig.palette.secondary.dark,
  },
  [`&.${classes.notClickable}`]: {},
})

const getAlertClick = (
  alert: Alert,
  onAlertClick?: (args: { fieldId: string; productId: string }) => void,
): (() => void) | undefined => {
  if (onAlertClick === undefined || alert.fieldId === FieldId.Unknown) {
    return undefined
  }

  return () =>
    onAlertClick?.({
      fieldId: alert.fieldId as string,
      productId: alert.productId,
    })
}

export interface UngroupedAlertProps {
  alert: Alert
  onClick?: (args: { fieldId: string; productId: string }) => void
}

export const UngroupedAlert = ({ alert, onClick }: UngroupedAlertProps) => {
  const onAlertClick = getAlertClick(alert, onClick)

  return (
    <Root
      className={clsx(classes.container, {
        [classes.notClickable]: onAlertClick === undefined,
      })}
      tabIndex={0}
      onClick={onAlertClick}
    >
      <td style={{ verticalAlign: 'middle' }} className={classes.cell}>
        {alert.fieldName !== undefined && (
          <Typography
            variant="body1"
            style={{
              color: ThemeConfig.palette.primary.main,
              textDecoration: 'underline',
            }}
            component="span"
          >
            {alert.fieldName}
          </Typography>
        )}
        <Typography
          variant="body2"
          className={classes.typographyColor}
          component="span"
        >
          {alert.fieldName !== undefined && ' - '}
          {alert.message}
        </Typography>
        <Typography variant="body1">{alert.productDescription}</Typography>
        <Typography variant="body2" className={classes.typographyColor}>
          {`${alert.productDisplayIdLabel}: ${alert.productDisplayId}`}
        </Typography>
      </td>
    </Root>
  )
}
